import { defineStore } from 'pinia'
import {
  ApiError,
  MalformedEventError,
  fetchCatalogue,
  streamTurn
} from '@shared/views/ai-chat/api/agent-chat'
import { useAuthStore } from '@shared/store/auth'
import { trackQuestion } from '@shared/views/ai-chat/utils/analytics'
import { applyEvent, newTurn } from '@shared/views/ai-chat/utils/turn'
import type {
  Catalogue,
  GateErrorKey,
  KnownErrorCode,
  Turn,
  TurnError
} from '@shared/views/ai-chat/types'

/* gate errors are stored as locale keys under aichat.main.errors, the view translates them */
const gateErrorKey = (error: unknown): GateErrorKey => {
  if (error instanceof ApiError && error.status === 403) return 'forbidden'
  if (error instanceof ApiError && error.status === 401) return 'unauthorized'
  return 'unreachable'
}

/* the code of a refusal that carries no `errorCode`, by its status */
const STATUS_ERROR_CODES: Readonly<Record<number, KnownErrorCode>> = {
  401: 'unauthorized',
  403: 'forbidden',
  422: 'validation',
  429: 'PLATFORM_RATE_LIMITED',
  502: 'PLATFORM_UNAVAILABLE',
  504: 'PLATFORM_TIMEOUT'
}

/* asking again cannot change the answer to these */
const FINAL_ERROR_CODES: ReadonlySet<string> = new Set([
  'FEATURE_INACTIVE',
  'forbidden',
  'validation',
  'PLATFORM_TOOLS_UNAVAILABLE'
])

/*
 * Refusals that are the account's or the session's state, not a fault: the drawer shows them as such (upsell,
 * not permitted, logged out, rate limit) or recovers on its own (a new conversation), so they are not logged.
 */
const EXPECTED_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'aborted',
  'FEATURE_INACTIVE',
  'forbidden',
  'unauthorized',
  'CONVERSATION_NOT_FOUND',
  'PLATFORM_RATE_LIMITED'
])

/* a turn the service refused before its stream: the service's `errorCode`, else one named by the status */
const refusalCode = (error: unknown): string => {
  if (error instanceof MalformedEventError) return 'TURN_FAILED'
  if (!(error instanceof ApiError)) return 'unreachable'
  return (
    error.body?.errorCode || STATUS_ERROR_CODES[error.status] || 'server_error'
  )
}

/* streamTurn throws a DOMException named AbortError on stop */
const isAbort = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  error.name === 'AbortError'

/* the view translates the code; the server's or the browser's message is never shown */
const turnError = (code: string): TurnError => ({
  scope: 'turn',
  code,
  message: '',
  retryable: !FINAL_ERROR_CODES.has(code)
})

/* the logged error names the step that failed; `cause` keeps the original for Sentry's linked errors */
const failure = (message: string, cause: unknown): Error =>
  Object.assign(new Error(message), { cause })

interface AgentChatState {
  /* the Dashboard tenant the conversation belongs to; syncTenant() drops it after an account switch */
  tenantId: string
  conversationId: string | undefined
  turns: Turn[]
  catalogue: Catalogue | undefined
  /* the account lacks the agent_chat feature: the service answered 403 FEATURE_INACTIVE */
  isUpsell: boolean
  gateError: GateErrorKey | ''
  controller: AbortController | undefined
  /* bumped by reset(): a request started under an older generation must not write any more */
  generation: number
}

/*
 * The Ask Tillhub conversation: in-session continuity and a New chat reset, ported from the
 * merchant-agent-sample-app chat store. The tenant is the logged-in account; requests carry the base API's token.
 */
export const useAgentChatStore = defineStore('agentChat', {
  state: (): AgentChatState => ({
    tenantId: '',
    conversationId: undefined,
    turns: [],
    catalogue: undefined,
    isUpsell: false,
    gateError: '',
    controller: undefined,
    generation: 0
  }),
  getters: {
    isStreaming: (state): boolean => state.turns.at(-1)?.status === 'streaming',
    hasTurns: (state): boolean => state.turns.length > 0
  },
  actions: {
    /* resets the conversation, aborting its stream, when the Dashboard now shows another tenant; true when it did */
    syncTenant(): boolean {
      const user: unknown = useAuthStore().user
      const tenantId = typeof user === 'string' ? user : ''
      if (tenantId === this.tenantId) return false
      this.reset()
      this.tenantId = tenantId
      this.catalogue = undefined
      this.isUpsell = false
      return true
    },
    /* logout: abort the stream and keep the generation rising, so a request from the old session cannot write */
    $globalReset(): void {
      this.controller?.abort()
      const generation = this.generation + 1
      this.$reset()
      this.generation = generation
    },
    reset(): void {
      this.generation++
      this.controller?.abort()
      this.controller = undefined
      this.conversationId = undefined
      this.turns = []
      this.gateError = ''
    },
    handleGateError(error: unknown): void {
      if (
        error instanceof ApiError &&
        error.body?.errorCode === 'FEATURE_INACTIVE'
      ) {
        this.isUpsell = true
        return
      }
      const key = gateErrorKey(error)
      this.gateError = key
      /* 401 and 403 are the session's and the account's state, shown as such */
      if (key === 'unreachable') {
        this.$logException(
          failure('Agent chat: loading the catalogue failed', error)
        )
      }
    },
    /* what a refused turn changes beyond its own error */
    handleTurnRefusal(error: unknown): void {
      if (!(error instanceof ApiError)) return
      if (error.body?.errorCode === 'FEATURE_INACTIVE') this.isUpsell = true
      /* the next question, or a resend, opens a new conversation */
      if (error.body?.errorCode === 'CONVERSATION_NOT_FOUND') {
        this.conversationId = undefined
      }
    },
    async loadCatalogue(): Promise<void> {
      this.syncTenant()
      this.isUpsell = false
      this.gateError = ''
      /* a response for a tenant the Dashboard no longer shows must not write */
      const { tenantId } = this
      try {
        const catalogue = await fetchCatalogue({ tenantId })
        if (this.tenantId !== tenantId) return
        this.catalogue = catalogue
      } catch (error: unknown) {
        if (this.tenantId !== tenantId) return
        this.catalogue = undefined
        /* the catalogue route answers 501 until UNTIL-22866; the chat works without it */
        if (error instanceof ApiError && error.status === 501) return
        this.handleGateError(error)
      }
    },
    /* `locale` is the dashboard locale the answer is written in, e.g. 'de-DE' */
    async send(
      prompt: string,
      { locale }: { locale?: string } = {}
    ): Promise<void> {
      this.syncTenant()
      const text = (prompt || '').trim()
      if (!text || this.isStreaming) return

      this.turns.push(newTurn(text))
      const index = this.turns.length - 1
      const controller = new AbortController()
      this.controller = controller
      /* every write below is for this request only: not after a reset, not over a newer request */
      const { generation } = this
      const isLive = (): boolean => this.generation === generation
      const turnAt = (): Turn => {
        const turn = this.turns[index]
        if (!turn) throw new Error(`Agent chat: no turn at ${index}`)
        return turn
      }

      try {
        await streamTurn({
          tenantId: this.tenantId,
          conversationId: this.conversationId,
          prompt: text,
          locale,
          signal: controller.signal,
          onEvent: (event) => {
            if (!isLive()) return
            if (event.event === 'conversation') {
              this.conversationId = event.data.conversation_id
            }
            this.turns[index] = applyEvent(turnAt(), event)
          }
        })
        if (!isLive()) return
        if (turnAt().status === 'streaming') {
          this.turns[index] = applyEvent(turnAt(), {
            event: 'error',
            data: turnError('TURN_FAILED')
          })
        }
      } catch (error: unknown) {
        if (!isLive()) return
        const code = isAbort(error) ? 'aborted' : refusalCode(error)
        if (code !== 'aborted') this.handleTurnRefusal(error)
        if (!EXPECTED_REFUSAL_CODES.has(code)) {
          this.$logException(
            failure(`Agent chat: the turn failed with ${code}`, error)
          )
        }
        this.turns[index] = applyEvent(turnAt(), {
          event: 'error',
          data: turnError(code)
        })
      } finally {
        if (this.controller === controller) this.controller = undefined
        /* a reset (new chat, account switch, logout) discards the turn, so it is not counted */
        if (isLive()) trackQuestion(this.tenantId, turnAt())
      }
    },
    stop(): void {
      this.controller?.abort()
    },
    resend(turn: Pick<Turn, 'prompt'>, options?: { locale?: string }) {
      return this.send(turn.prompt, options)
    }
  }
})
